import * as React from "react";

import { invariant } from "../lib/misc";

/**
 * Lightweight wrapper around `React.createContext` that requires a
 * `displayName`, which is useful for debugging in React DevTools.
 */
export function createContext<ValueType>(
  displayName: string,
  initialValue: ValueType,
): React.Context<ValueType>;
export function createContext<ValueType>(
  displayName: string,
  initialValue: ValueType | null,
): React.Context<ValueType | null>;
export function createContext<ValueType>(
  displayName: string,
): React.Context<ValueType | undefined>;

// NOTE (Chance 2024-06-20): `@__NO_SIDE_EFFECTS__` is an esbuild and rollup
// convention to indicate that this function has no side effects. By default
// esbuild might not tree-shake code that relies on this function because it
// sets `displayName`, and the bundler doesn't know whether that has side
// effects (ex, if there's a setter for the property). This annotation tells the
// bundler it's tree-shakable. Note too that the position in which the comment
// appears is important!
// https://github.com/evanw/esbuild/issues/3149#issuecomment-1585820844

/* @__NO_SIDE_EFFECTS__ */ export function createContext<ValueType>(
  displayName: string,
  initialValue?: ValueType,
) {
  const ctx = React.createContext(initialValue);
  ctx.displayName = displayName;
  return ctx;
}

export function useRequiredContext<ValueType>(
  context: React.Context<ValueType>,
): NonNullable<ValueType> {
  const ctx = React.useContext(context);
  // Inline nullish check (not isNotNullish): invariant(asserts condition) only
  // narrows from the condition expression itself, not from a boolean helper.
  invariant(
    ctx !== null && ctx !== undefined,
    `useRequiredContext: ${context.displayName ?? "Unknown context"} is not defined. Caller component must be rendered in a provider.`,
  );
  return ctx;
}
